import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { AppButton, AppTextField } from '../../../components/design-system';
import { opsDevicesService } from '../../../services/OpsDevicesService';
import { getDeviceCategoryFilterLabel, POSITIONING_DEVICE_ID_HELPER } from '@cnapse/shared';
import type { OpsDeviceDetail } from '@cnapse/shared/schemas.js';
import { sanitizePemCertificateInput } from '@cnapse/shared/pem-utils.js';

const IOT_CATEGORIES = new Set(['efem-gateway', 'location-gateway']);
const LOCATION_CATEGORIES = new Set(['location-anchor', 'location-tag']);

function canRotateAkCert(device: OpsDeviceDetail): boolean {
  return device.iotProvisioningStatus === 'not_provisioned'
    && device.inventoryStatus === 'in-stock';
}

function buildDeviceUpdatePayload(
  form: typeof EMPTY_FORM,
  device: OpsDeviceDetail,
  options: { isLocation: boolean; canEditAkCert: boolean },
): Parameters<typeof opsDevicesService.updateNonIotDevice>[1] {
  const payload: Parameters<typeof opsDevicesService.updateNonIotDevice>[1] = {
    serialNumber: form.serialNumber !== device.serialNumber ? form.serialNumber : undefined,
    deviceCategory: form.deviceCategory || undefined,
    macAddress: form.macAddress || undefined,
    vendorSerialNumber: form.vendorSerialNumber || undefined,
    manufacturerName: form.manufacturerName || undefined,
    model: form.model || undefined,
    mfgBatch: form.mfgBatch || undefined,
    mfgDate: form.mfgDate || undefined,
    vendorDeviceId: options.isLocation ? (form.vendorDeviceId || undefined) : undefined,
    notes: form.notes || undefined,
  };

  if (options.canEditAkCert && form.akCertPem.trim()) {
    const sanitizedAk = sanitizePemCertificateInput(form.akCertPem);
    if (sanitizedAk !== (device.akCertPem ?? '')) {
      payload.akCertPem = sanitizedAk;
    }
  }

  return payload;
}

type DeviceType = 'non-iot' | 'iot';

interface Props {
  open: boolean;
  /** 'add' shows the full registration form; 'edit' pre-populates and locks immutable fields. */
  mode: 'add' | 'edit';
  device?: OpsDeviceDetail | null;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

const EMPTY_FORM = {
  serialNumber: '',
  vendorSerialNumber: '',
  deviceCategory: 'location-anchor',
  macAddress: '',
  manufacturerName: '',
  ekCertPem: '',
  akCertPem: '',
  model: '',
  mfgBatch: '',
  mfgDate: new Date().toISOString().slice(0, 10),
  vendorDeviceId: '',
  notes: '',
};

export default function DeviceFormDialog({ open, mode, device, onClose, onSuccess }: Props) {
  const [deviceType, setDeviceType] = useState<DeviceType>('non-iot');
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-populate when dialog opens
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && device) {
      const isIot = IOT_CATEGORIES.has(device.deviceCategory ?? '');
      setDeviceType(isIot ? 'iot' : 'non-iot');
      setForm({
        serialNumber: device.serialNumber ?? '',
        vendorSerialNumber: device.vendorSerialNumber ?? '',
        deviceCategory:   device.deviceCategory    ?? 'location-anchor',
        macAddress:       device.macAddress        ?? '',
        manufacturerName: device.manufacturerName  ?? '',
        ekCertPem:        '',
        akCertPem:      device.akCertPem        ?? '',
        model:            device.deviceModel          ?? '',
        mfgBatch:         device.mfgBatch          ?? '',
        mfgDate:          device.mfgDate           ?? '',
        vendorDeviceId:   device.vendorDeviceId    ?? '',
        notes:            (device as Record<string, unknown>).notes as string ?? '',
      });
    } else {
      setDeviceType('non-iot');
      setForm({ ...EMPTY_FORM, mfgDate: new Date().toISOString().slice(0, 10) });
    }
    setError(null);
  }, [open, mode, device]);

  const set = (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const isEdit = mode === 'edit';
  const isIot = deviceType === 'iot';
  const isLocation = LOCATION_CATEGORIES.has(form.deviceCategory);
  const canEditSerialNumber = isEdit && device && (
    (IOT_CATEGORIES.has(device.deviceCategory ?? '') && device.iotProvisioningStatus === 'not_provisioned') ||
    (!IOT_CATEGORIES.has(device.deviceCategory ?? '') && device.inventoryStatus === 'in-stock')
  );
  const canEditAkCert = Boolean(isEdit && device && IOT_CATEGORIES.has(device.deviceCategory ?? '') && canRotateAkCert(device));

  const handleSave = async () => {
    setError(null);

    if (!form.vendorSerialNumber || !form.deviceCategory || !form.model || !form.mfgBatch || !form.mfgDate) {
      setError('Vendor Serial Number, Category, Model, Mfg Batch, and Mfg Date are required.');
      return;
    }
    if (!isEdit && !isIot && !form.macAddress) {
      setError('MAC Address is required for non-IoT devices.');
      return;
    }
    if (!isEdit && isIot && !form.ekCertPem.trim()) {
      setError('EK Certificate (PEM) is required for IoT devices.');
      return;
    }
    if (!isEdit && isIot && !form.akCertPem.trim()) {
      setError('AK Certificate (PEM) is required for IoT devices.');
      return;
    }
    if (!isEdit && !isIot && isLocation && !form.vendorDeviceId) {
      setError('Peripheral Device ID is required for location devices.');
      return;
    }

    setLoading(true);
    try {
      if (isEdit && device) {
        await opsDevicesService.updateNonIotDevice(
          device.serialNumber,
          buildDeviceUpdatePayload(form, device, { isLocation, canEditAkCert }),
        );
        onSuccess(`${device.serialNumber} updated successfully.`);
      } else if (!isIot) {
        await opsDevicesService.registerNonIotDevice({
          serialNumber: form.serialNumber || undefined,
          vendorSerialNumber: form.vendorSerialNumber,
          macAddress:       form.macAddress,
          deviceCategory:   form.deviceCategory,
          manufacturerName: form.manufacturerName || undefined,
          model:            form.model,
          mfgBatch:         form.mfgBatch,
          mfgDate:          form.mfgDate,
          vendorDeviceId:   form.vendorDeviceId   || undefined,
          notes:            form.notes            || undefined,
        });
        onSuccess(`${form.serialNumber || form.vendorSerialNumber} registered successfully.`);
      } else {
        // Register IoT
        await opsDevicesService.registerIotDevice({
          serialNumber: form.serialNumber || undefined,
          vendorSerialNumber: form.vendorSerialNumber,
          ekCertPem: sanitizePemCertificateInput(form.ekCertPem),
          akCertPem: sanitizePemCertificateInput(form.akCertPem),
          manufacturerName: form.manufacturerName || undefined,
          model:            form.model,
          mfgBatch:         form.mfgBatch,
          mfgDate:          form.mfgDate,
          deviceCategory:   form.deviceCategory as 'efem-gateway' | 'location-gateway',
          macAddress:       form.macAddress       || undefined,
          notes:            form.notes            || undefined,
        });
        onSuccess(`${form.serialNumber || form.vendorSerialNumber} registered successfully.`);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? `Edit Device — ${device?.serialNumber}` : 'Register Device'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {isEdit && (
            <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
              <strong>Edit Note:</strong> Cnapse serial can only be changed for newly created devices (in-stock/manufactured state). Device category follows the same restriction.
            </Alert>
          )}

          {/* Device type toggle — add mode only */}
          {!isEdit && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Registration Type
              </Typography>
              <ToggleButtonGroup
                value={deviceType}
                exclusive
                onChange={(_e, val) => {
                  if (!val) return;
                  setDeviceType(val as DeviceType);
                  setForm((f) => ({
                    ...f,
                    deviceCategory: val === 'iot' ? 'efem-gateway' : 'location-anchor',
                    ekCertPem: '',
                  }));
                  setError(null);
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value="non-iot" sx={{ flex: 1 }}>Non-IoT Device</ToggleButton>
                <ToggleButton value="iot" sx={{ flex: 1 }}>IoT Gateway Device</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Device Category */}
          <AppTextField
            select
            label="Device Category"
            value={form.deviceCategory}
            onChange={(e) => setForm((f) => ({ ...f, deviceCategory: e.target.value }))}
            size="small"
            fullWidth
            disabled={isEdit && (IOT_CATEGORIES.has(device?.deviceCategory ?? '') ? device?.iotProvisioningStatus !== 'not_provisioned' : device?.inventoryStatus !== 'in-stock')}
            helperText={isEdit && (IOT_CATEGORIES.has(device?.deviceCategory ?? '') ? device?.iotProvisioningStatus !== 'not_provisioned' : device?.inventoryStatus !== 'in-stock') ? 'Cannot change category after provisioning' : undefined}
          >
            {isIot ? [
              <MenuItem key="efem-gateway" value="efem-gateway">{getDeviceCategoryFilterLabel('efem-gateway')}</MenuItem>,
              <MenuItem key="location-gateway" value="location-gateway">{getDeviceCategoryFilterLabel('location-gateway')}</MenuItem>,
            ] : [
              <MenuItem key="location-anchor" value="location-anchor">{getDeviceCategoryFilterLabel('location-anchor')}</MenuItem>,
              <MenuItem key="location-tag" value="location-tag">{getDeviceCategoryFilterLabel('location-tag')}</MenuItem>,
              <MenuItem key="barcode-scanner" value="barcode-scanner">{getDeviceCategoryFilterLabel('barcode-scanner')}</MenuItem>,
              <MenuItem key="handheld-barcode-scanner" value="handheld-barcode-scanner">{getDeviceCategoryFilterLabel('handheld-barcode-scanner')}</MenuItem>,
              <MenuItem key="waferid-camera" value="waferid-camera">{getDeviceCategoryFilterLabel('waferid-camera')}</MenuItem>,
            ]}
          </AppTextField>

          {/* Serial Number — disabled in edit unless in early state */}
          <AppTextField
            label={isEdit ? 'Cnapse Serial Number' : 'Cnapse Serial Number (Optional)'}
            value={form.serialNumber}
            onChange={set('serialNumber')}
            size="small"
            fullWidth
            disabled={isEdit && !canEditSerialNumber}
            placeholder="e.g. CNAM7X9K2P"
            helperText={
              isEdit && !canEditSerialNumber
                ? 'Cnapse serial cannot be changed after provisioning'
                : isEdit && canEditSerialNumber
                ? 'You can still correct this Cnapse serial'
                : 'Leave blank to auto-generate CNxxxxxxxxxx'
            }
          />

          <AppTextField
            label={isEdit ? 'Vendor Serial Number' : 'Vendor Serial Number *'}
            value={form.vendorSerialNumber}
            onChange={set('vendorSerialNumber')}
            size="small"
            fullWidth
            placeholder="e.g. UA330025W450042"
            helperText="Vendor-provided hardware serial"
          />

          {/* MAC Address */}
          <AppTextField
            label={(!isEdit && !isIot) ? 'MAC Address *' : 'MAC Address'}
            value={form.macAddress}
            onChange={set('macAddress')}
            size="small"
            fullWidth
            placeholder="e.g. AA:BB:CC:DD:EE:FF"
          />

          {/* Manufacturer Name */}
          <AppTextField
            label="Manufacturer Name"
            value={form.manufacturerName}
            onChange={set('manufacturerName')}
            size="small"
            fullWidth
            placeholder="e.g. Woxu"
          />

          {/* EK / AK — IoT registration or in-stock AK rotation */}
          {!isEdit && isIot && (
            <>
              <AppTextField
                label="EK Certificate (PEM) *"
                value={form.ekCertPem}
                onChange={set('ekCertPem')}
                size="small"
                fullWidth
                multiline
                minRows={3}
                placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
              />
              <AppTextField
                label="AK Certificate (PEM) *"
                value={form.akCertPem}
                onChange={set('akCertPem')}
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                helperText="The Attestation Key certificate from the TPM"
              />
            </>
          )}

          {canEditAkCert && device && (
            <>
              <AppTextField
                label="EK Hash (read-only)"
                value={device.ekHash ?? '—'}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="Hardware-bound TPM identity — does not change after OS reimage"
              />
              <AppTextField
                label="AK Certificate (PEM) *"
                value={form.akCertPem}
                onChange={set('akCertPem')}
                size="small"
                fullWidth
                multiline
                minRows={3}
                placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                helperText="Only editable while in-stock (after Reset for Redeployment). Re-run cm-register-batch.ps1 on the device, then paste the new AK cert here before allocating to an order."
              />
            </>
          )}

          {/* Model */}
          <AppTextField
            label={isEdit ? 'Model' : 'Model *'}
            value={form.model}
            onChange={set('model')}
            size="small"
            fullWidth
            placeholder="e.g. UA-330"
          />

          {/* Manufacturing Batch */}
          <AppTextField
            label={isEdit ? 'Manufacturing Batch' : 'Manufacturing Batch *'}
            value={form.mfgBatch}
            onChange={set('mfgBatch')}
            size="small"
            fullWidth
            placeholder="e.g. BATCH_APR_2026"
          />

          {/* Manufacturing Date */}
          <AppTextField
            label={isEdit ? 'Manufacturing Date' : 'Manufacturing Date *'}
            type="date"
            value={form.mfgDate}
            onChange={set('mfgDate')}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          {/* Vendor Device ID */}
          <AppTextField
            label={isLocation && !isEdit ? 'Vendor Device ID *' : 'Vendor Device ID'}
            value={form.vendorDeviceId}
            onChange={set('vendorDeviceId')}
            size="small"
            fullWidth
            placeholder="e.g. BD125"
            helperText={
              isLocation
                ? POSITIONING_DEVICE_ID_HELPER
                : "Vendor's internal device identifier"
            }
          />

          {/* Notes */}
          <AppTextField
            label="Notes (optional)"
            value={form.notes}
            onChange={set('notes')}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <AppButton onClick={onClose} disabled={loading}>Cancel</AppButton>
        <AppButton
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {isEdit ? 'Save Changes' : 'Register'}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}
